import { useState, useEffect } from "react";
import imgBackgroundBlur from "../../public/assets/optimized/996d311dc7da3e0e3a63b62edfbc322af3523681.webp";
import imgComputer071 from "../../public/assets/optimized/16d8346c8438782e9bfb78b155e7639d4319eed0.webp";
import imgIndex23 from "../../public/assets/optimized/31adfb68ce7d78042722a289dce87ef1b3da4655.webp";
import imgTapeMockup from "../../public/assets/optimized/514228b643f29279f45e6abfbc21bc841461407b.webp";

function ScrambleText({
  initialText,
  duration = 30000,
}: {
  initialText: string;
  duration?: number;
}) {
  const [displayText, setDisplayText] = useState(initialText);
  const [isScrambling, setIsScrambling] = useState(true);

  useEffect(() => {
    if (!isScrambling) return;

    const chars = [
      "*",
      "^",
      "!",
      "@",
      "#",
      "$",
      "%",
      "&",
      "(",
      ")",
      "-",
      "+",
      "=",
      "~",
    ];
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= duration) {
        // After 30 seconds, set all to asterisks
        setDisplayText("*".repeat(initialText.length));
        setIsScrambling(false);
        clearInterval(interval);
        return;
      }

      // Scramble effect
      const scrambled = initialText
        .split("")
        .map((char, i) => {
          // Gradually convert more characters to * as time passes
          const progress = elapsed / duration;
          if (Math.random() < progress) {
            return "*";
          }
          // Random scramble for remaining characters
          return Math.random() > 0.3
            ? char
            : chars[Math.floor(Math.random() * chars.length)];
        })
        .join("");

      setDisplayText(scrambled);
    }, 100); // Update every 100ms

    return () => clearInterval(interval);
  }, [initialText, duration, isScrambling]);

  return <span>{displayText}</span>;
}

export default function MissionCtrl() {
  return (
    <div className="relative flex flex-col items-center bg-[#fffbfb] w-full min-h-screen overflow-x-hidden">
      <div className="absolute inset-0 blur-[125px] filter">
        <img
          alt=""
          className="absolute w-full h-full object-cover"
          src={imgBackgroundBlur}
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-[rgba(0,0,0,0.3)]" />
      </div>

      <div className="top-[150px] sm:top-[180px] md:top-[200px] lg:top-[225px] absolute inset-0 flex flex-col items-center px-4 sm:px-6 md:px-[30px] pt-[10px]">
        <div className="flex-grow bg-[#fffbfb] blur-2xl rounded-tl-[50px] sm:rounded-tl-[75px] lg:rounded-tl-[100px] rounded-tr-[20px] w-full max-w-[1600px] h-full filter" />
      </div>

      <header className="z-10 relative flex items-start px-4 sm:px-8 md:px-16 lg:px-[100px] pt-4 sm:pt-6 md:pt-[30px] w-full max-w-[1500px] h-auto min-h-[100px] sm:min-h-[150px] md:min-h-[200px] lg:h-[268px]">
        <h2 className="font-advercase-regular text-[#2c3831] text-lg sm:text-xl md:text-[24px] tracking-[-0.72px]">
          Mission Ctrl, Inc
        </h2>
      </header>

      <main className="z-10 relative flex flex-col items-center w-full">
        <section className="flex flex-col items-center gap-6 sm:gap-10 lg:gap-[50px] px-6 sm:px-8 md:px-16 lg:px-[100px] pt-16 sm:pt-12 md:pt-10 lg:pt-[35px] pb-8 sm:pb-10 lg:pb-[50px] rounded-tl-[50px] sm:rounded-tl-[75px] lg:rounded-tl-[100px] rounded-tr-[20px] w-full max-w-[1200px]">
          <div className="flex flex-col items-start gap-6 sm:gap-10 lg:gap-[50px] w-full">
            <h1 className="w-full pr-4 sm:pr-0 font-advercase-bold text-3xl xs:text-4xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[128px] text-black leading-[1.1] sm:leading-[1.2] md:leading-[1.3] tracking-[-0.5px] xs:tracking-[-1px] sm:tracking-[-2px] lg:tracking-[-3.84px] break-words">
              Set the stage for the future.
            </h1>
          </div>

          <div className="flex flex-col items-start gap-4 sm:gap-[20px] w-full">
            <h2 className="font-berkeley-mono text-[#2c3831] text-base sm:text-lg md:text-[20px] uppercase tracking-[-0.6px]">
              Mission Ctrl Core Focus:
            </h2>
            <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-[20px] w-full">
              <div className="w-full sm:w-3/4 md:w-2/3 lg:w-[450px] mx-auto lg:mx-0 lg:order-2">
                <img
                  alt="Computer illustration"
                  className="w-full h-auto object-contain"
                  src={imgComputer071}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <ul className="space-y-2 pl-6 sm:pl-9 w-full lg:w-[415px] font-advercase-regular text-[#2c3831] text-lg sm:text-xl md:text-[24px] tracking-[-0.72px] list-disc lg:order-1">
                <li className="leading-[1.3]">Command surfaces</li>
                <li className="leading-[1.3]">Multi-dimensional routing</li>
                <li className="leading-[1.3]">Deterministic flow control</li>
                <li className="leading-[1.3]">Windows as waypoints</li>
                <li className="leading-[1.3]">Spatial orchestration</li>
                <li className="leading-[1.3]">Latency as enemy</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-center items-center py-8 sm:py-12 lg:py-[59px] w-full px-6 sm:px-8 md:px-12">
            <div className="space-y-4 w-full max-w-[1046px] font-advercase-bold text-[#2c3831] text-base sm:text-lg md:text-[20px] tracking-[-0.6px]">
              <p className="break-words hyphens-auto">
                Mission Ctrl makes software that rethinks how people move
                through their digital environment. Our focus is on building
                tools that feel immediate, where actions carry weight, and
                transitions hold context, and flow is never broken.
              </p>
              <p className="break-words hyphens-auto">
                It's about designing systems that don't just respond, but
                anticipate—making navigation as natural as thought. By bending
                complexity into simplicity, we create spaces where focus stays
                intact and work feels uninterrupted.
              </p>
              <p className="break-words hyphens-auto">
                Mission Ctrl builds not for spectacle, but for control, rhythm,
                and persistence. The result is a framework where operators can
                chart their own course—confident that the system will keep pace.
              </p>
            </div>
          </div>
        </section>

        <section className="relative flex flex-col lg:flex-row justify-center items-start gap-6 sm:gap-10 lg:gap-[50px] px-4 sm:px-8 md:px-16 lg:px-[100px] pt-8 sm:pt-10 lg:pt-[50px] w-full max-w-[1200px]">
          <div className="top-0 absolute inset-x-0 border-[#cbcbcb] border-t" />
          <div className="w-full sm:w-2/3 md:w-1/2 lg:w-[267px] mb-6 lg:mb-0 mx-auto lg:mx-0">
            <img
              alt="People laying in park"
              className="w-full h-auto max-h-[200px] sm:max-h-[250px] lg:h-[267px] object-contain lg:object-cover rounded-lg"
              src={imgIndex23}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="flex flex-col flex-grow w-full">
            <div className="flex flex-col gap-4 sm:gap-6 lg:gap-[30px] pb-8 sm:pb-10 lg:pb-[50px]">
              <h2 className="font-source-code text-[#2c3831] text-xs sm:text-[12px] uppercase tracking-[-0.36px]">
                2025 SYSTEM EVENTS
              </h2>
              <ul className="flex flex-col">
                {[
                  {
                    date: "1.23.25",
                    title: "Boot Sequence Initialized",
                    status: "*********",
                    scramble: true,
                  },
                  {
                    date: "1.24.25",
                    title: "Context Buffer Restored",
                    status: "*********",
                    scramble: true,
                  },
                  {
                    date: "1.26.25",
                    title: "Context Buffer Restored",
                    status: "*********",
                    scramble: true,
                  },
                  {
                    date: "1.27.25",
                    title: "Parallel Session Spun Up",
                    status: "*********",
                    scramble: true,
                  },
                  {
                    date: "1.30.25",
                    title: "Persistence Layer Engaged",
                    status: "*********",
                    scramble: true,
                  },
                  {
                    date: "1.31.25",
                    title: "Drift Sync Completed",
                    status: "*********",
                    scramble: true,
                  },
                ].map((event, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 sm:gap-4 px-1 sm:px-2 py-2 sm:py-2.5 min-h-[30px] sm:h-auto"
                  >
                    <a
                      href="#"
                      className="flex flex-col sm:flex-row w-full font-source-code text-[#2c3831] text-xs sm:text-[13px] tracking-[-0.39px] gap-1 sm:gap-0"
                    >
                      <span className="whitespace-nowrap shrink-0">{event.date}</span>
                      <span className="flex-grow px-0 sm:px-4 break-words">{event.title}</span>
                      <span className="font-mono whitespace-nowrap text-xs sm:text-[13px] shrink-0">
                        {event.scramble ? (
                          <ScrambleText initialText={event.status} />
                        ) : (
                          event.status
                        )}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="relative flex flex-col gap-6 sm:gap-8 lg:gap-[40px] px-4 sm:px-8 md:px-16 lg:px-[100px] pt-6 sm:pt-8 lg:pt-[30px] pb-16 sm:pb-20 lg:pb-[100px] w-full max-w-[1200px]">
          <div className="top-0 absolute inset-x-0 border-[#cbcbcb] border-t" />
          <h2 className="font-source-code text-[#2c3831] text-xs sm:text-[12px] uppercase tracking-[-0.36px]">
            Bios
          </h2>
          <div className="flex flex-col gap-6 sm:gap-8 lg:gap-[40px] font-source-code text-[#2c3831] text-xs sm:text-[13px] tracking-[-0.39px]">
            <div className="space-y-2">
              <p className="font-source-code-bold">
                Andrew Armenante (Engineering)
              </p>
              <p>Melodic storyteller with raw emotion and vinyl passion.</p>
            </div>
            <div className="space-y-2">
              <p className="font-source-code-bold">Eadon Jacobs (Product)</p>
              <p>Cinematic influences shape his sonic textures.</p>
            </div>
          </div>
        </section>

        <section className="flex flex-col items-center gap-6 sm:gap-10 lg:gap-[50px] px-4 sm:px-8 md:px-16 lg:px-[100px] pt-6 sm:pt-8 lg:pt-[30px] w-full max-w-[1200px]">
          <div className="relative flex flex-col gap-6 sm:gap-8 lg:gap-[40px] pt-6 sm:pt-8 lg:pt-[30px] pb-16 sm:pb-20 lg:pb-[100px] w-full">
            <div className="top-0 absolute inset-x-0 border-[#cbcbcb] border-t" />
            <h2 className="font-source-code-bold text-[#2c3831] text-xs sm:text-[12px] uppercase tracking-[-0.36px]">
              DISCOVER WHAT IT MEANS TO BE FREE.
            </h2>
          </div>
          <div className="top-0 sticky w-full h-[250px] sm:h-[350px] md:h-[450px] lg:h-[505px]">
            <img
              alt="Recording tape with image of people on a beach on it"
              className="w-full h-full object-contain rotate-270"
              src={imgTapeMockup}
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>
      </main>

      <footer className="relative flex flex-col gap-6 sm:gap-[30px] px-4 sm:px-8 md:px-16 lg:px-[100px] pt-16 sm:pt-20 lg:pt-[100px] pb-12 sm:pb-16 lg:pb-[80px] w-full max-w-[1200px]">
        <div className="top-0 absolute inset-x-0 border-[#cbcbcb] border-t" />
        <div className="relative flex flex-col items-center gap-2 pt-6 sm:pt-[30px] text-center">
          <p className="font-source-code text-[#2c3831] text-[10px] sm:text-[12px] tracking-[-0.36px]">
            Mission Ctrl, Inc | 201 Folsom Street, Apt 25c
          </p>
          <p className="font-source-code text-[#2c3831] text-[10px] sm:text-[12px] tracking-[-0.36px]">
            San Francisco, CA 94105
          </p>
        </div>
      </footer>
    </div>
  );
}
